import { App } from './App';
import { ApplicationProvider } from './hooks/useApplication';
import { AppMap, routes } from './routes';
import { AuthProvider } from './hooks/useAuth';
import { Base64 } from 'js-base64';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ClientConstraintsProvider } from './hooks/useClientConstraints';
import { ClientOfferProvider } from './hooks/useClientOffer';
import { ERROR_QUERY_PARAM, SESSION_TINK_REASON } from 'utils/constants';
import { fakeDataConfig } from './utils/fakeDataConfig';
import { handleGTMModalVisibility } from 'utils/gtmModal';
import { isNotProduction, isNotStage } from './utils/isNotProduction';
import { RecoilRoot } from 'recoil';
import { TestingTools } from 'InternalLibrary';
import { useAnalyticsCookies } from './hooks/useAnalyticsCookies';
import { useGetClientApplication } from 'api';
import { useLocation } from 'react-router-dom';
import DataLayer from './utils/dataLayer/DataLayer';
import EventCollectorContextProvider from 'hooks/eventCollector/EventCollectorContextProvider';
import NoBEValidationsBanner from 'components/Router/NoBEValidationsBanner/NoBEValidationsBanner';
import PageEvent from './hooks/eventCollector/events/PageEvent';
import React, {
  lazy,
  PropsWithChildren,
  Suspense,
  useEffect,
  useState,
} from 'react';
import useClient, { ClientProvider } from './hooks/useClient';
import useEventCollector from './hooks/eventCollector/useEventCollector';
import useSmartlook from './hooks/useSmartlook';

const LazyBuildInfo = lazy(() => import('./utils/BuildInfo'));

export default function Router() {
  const isDevEnv = isNotProduction();
  return (
    <RecoilRoot>
      <App.Provider>
        <EventCollectorContextProvider>
          <AuthProvider>
            <ClientProvider>
              <ApplicationProvider>
                <ClientConstraintsProvider>
                  <ClientOfferProvider>
                    <BrowserRouter>
                      <AppRoutes />
                      <DataLayer />
                      {isDevEnv && <TestingTools fakeData={fakeDataConfig} />}
                      <Suspense fallback={null}>
                        {isDevEnv && <LazyBuildInfo />}
                      </Suspense>
                    </BrowserRouter>
                  </ClientOfferProvider>
                </ClientConstraintsProvider>
              </ApplicationProvider>
            </ClientProvider>
          </AuthProvider>
        </EventCollectorContextProvider>
      </App.Provider>
    </RecoilRoot>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();
  const { page } = useEventCollector();
  const { identifyClient } = useSmartlook();
  const { initializeCookiesIfNeeded } = useAnalyticsCookies();

  useEffect(() => {
    page(new PageEvent());
  }, [pathname, page]);

  useEffect(() => {
    // Scroll to top after rendering
    window.scrollTo(0, 0);

    handleGTMModalVisibility();
    initializeCookiesIfNeeded();
  }, [pathname]);

  // Smartlook Identify Client
  useEffect(() => {
    if (!isNotProduction()) identifyClient();
  }, [identifyClient]);

  return (
    <Routes>
      {Object.keys(AppMap).map((path) => {
        const RouteComponent = AppMap[path].nonDynamicComponent;
        const authenticated: boolean | undefined = AppMap[path].authenticated;
        return (
          <Route
            key={path}
            path={path}
            element={
              <GuardRoute authenticated={authenticated}>
                <RouteComponent />
              </GuardRoute>
            }
          />
        );
      })}
    </Routes>
  );
}

function GuardRoute({
  children,
  authenticated,
}: PropsWithChildren<{
  available?: boolean;
  authenticated?: boolean;
}>) {
  const [availableFromBEValidations, setAvailableFromBEValidations] = useState<
    boolean | undefined
  >(true);
  const { data: application } = useGetClientApplication();
  const { client } = useClient();
  const { pathname: urlPath } = useLocation();

  const searchParams = new URLSearchParams(window.location.search);

  const readyForNextScreen =
    authenticated !== true || (Boolean(client) && Boolean(application));

  const isRouteAvailableAccordingtoBE = () => {
    if (!client || !application) return true;

    const isResolutionRejectedCancelled =
      client?.blocked ||
      application?.resolution === 'REJECTED' ||
      application?.resolution === 'CANCELLED';

    const isResolutionApproved = application.resolution === 'APPROVED';

    const MANUAL_TINK_REASONS = [
      'REQUEST_FAILED_CREATE_REPORT',
      'TINK_SUCCESS_AUTOREPAY',
      'BAD_REQUEST',
      'INTERNAL_ERROR',
      'TEMPORARY_ERROR',
    ];

    const isManualBecauseTink = MANUAL_TINK_REASONS.includes(
      Base64.decode(sessionStorage.getItem(SESSION_TINK_REASON) || ''),
    );

    const isManual = application.resolution === 'MANUAL' || isManualBecauseTink;

    const errorReasonExists = Boolean(searchParams.get(ERROR_QUERY_PARAM));

    switch (urlPath) {
      case routes.FirstLoan:
      case routes.FirstLoan_Email:
        return !client?.email;
      case routes.FirstLoan_Confirmation:
        return Boolean(client.email) && !client?.dateOfBirth;
      case routes.FirstLoan_PersonalData:
        return !client?.dateOfBirth;
      case routes.FirstLoan_Address:
        return (
          Boolean(client?.dateOfBirth) && !client?.declaredAddress?.postalCode
        );
      case routes.FirstLoan_Income:
        return Boolean(
          client?.declaredAddress?.postalCode &&
            !client?.employer?.employmentStatus,
        );
      case routes.FirstLoan_PhoneVerification:
      case routes.FirstLoan_IncorrectCode:
      case routes.FirstLoan_PhoneVerification_ChangePhone:
        return Boolean(client?.employer?.employmentStatus);
      case routes.FirstLoan_PhoneVerification_Check:
        return Boolean(client?.employer?.employmentStatus);
      case routes.FirstLoan_OnlineBank_Verify:
      case routes.FirstLoan_OnlineBank_Error:
      case routes.FirstLoan_OnlineBank_Check:
      case routes.FirstLoan_OnlineBank_AddDocuments:
      case routes.firstLoan_tink_bank_na:
      case routes.firstLoan_tink_bank_issue:
      case routes.firstLoan_tink_later:
      case routes.FirstLoan_OnlineBank_DocumentsRequired:
        return Boolean(
          (application?.confirmed || application.resolution === 'REJECTED') &&
            !isResolutionApproved,
        );
      case routes.FirstLoan_Manual:
        return Boolean(
          application?.confirmed && !isResolutionApproved && isManual,
        );
      case routes.FirstLoan_Manual_Upload:
        return Boolean(application?.confirmed && !isResolutionApproved);
      case routes.FirstLoan_Manual_Upload_BankAccount:
        return Boolean(client.hasAttachments);
      case routes.FirstLoan_OnlineBank_Iban:
        return Boolean(client.hasAttachments);
      case routes.FirstLoan_Manual_Upload_Pending:
        return Boolean(client.hasAttachments && client.bankAccount);
      case routes.FirstLoan_OnlineBank_Pending:
        return Boolean(client.bankAccount && client.hasAttachments);
      case routes.FirstLoan_OnlineBank_RejectedByTink:
        return Boolean(
          isResolutionRejectedCancelled &&
            application.resolutionDetail &&
            [
              'REJECTED_BY_THIRD_PARTY',
              'INSTANTOR_TOO_MANY_ACCOUNTS',
              'INSTANTOR_ID_MISMATCH',
            ].includes(application.resolutionDetail),
        );
      case routes.FirstLoan_Rejected:
        return (
          isResolutionRejectedCancelled || client?.blocked || errorReasonExists
        );
      case routes.Account_RepeatedLoan_Confirm:
      case routes.Account_RepeatedLoan_Loading:
        return !application.confirmed;
      case routes.Account_RepeatedLoan_RejectedApplication:
      case routes.Account_RepeatedLoan_ApprovedApplication:
        return Boolean(application.confirmed);
      case routes.Affiliate_FirstLoan_Application:
        return Boolean(
          application?.affiliateCooperationType === 'API' &&
            application?.status === 'OPEN' &&
            !application?.confirmed,
        );
      case routes.payment_success:
      case routes.payment_failed:
      case routes.FirstLoan_OnlineBank_Approved:
        return !isResolutionRejectedCancelled;
      case routes.best_offer:
        return application.resolutionDetail === 'NEW_OFFER';
      case routes.FirstLoan_Welcome:
        return true;
      case routes.OneTimeTokenLogin:
        return true;
      default:
        return undefined;
    }
  };

  const isAuthenticated = Boolean(sessionStorage.getItem('token'));

  const userAuthenticationValided =
    (authenticated === undefined && !isAuthenticated) ||
    authenticated === isAuthenticated;

  // useEffect to check BE properties to know if route is available
  useEffect(() => {
    const checkConditions = async () => {
      const nextAvailable = isRouteAvailableAccordingtoBE();
      if (readyForNextScreen && nextAvailable && userAuthenticationValided) {
        sessionStorage.setItem('lastAvailableLocation', urlPath);
      }
      if (nextAvailable !== availableFromBEValidations)
        setAvailableFromBEValidations(nextAvailable);
    };
    checkConditions();
  }, [readyForNextScreen, urlPath]);

  const safestRoute =
    sessionStorage.getItem('lastAvailableLocation') || routes.FirstLoan;

  // Navigate to the last safest route
  const isValidSafestRoute =
    safestRoute !== urlPath && safestRoute !== routes.OneTimeTokenLogin;

  // Redirect to initial Route
  if (urlPath === '/') {
    return <Navigate to={routes.FirstLoan} />;
  }

  // Navigate to the last safest route
  if (
    isValidSafestRoute &&
    (availableFromBEValidations === false || !userAuthenticationValided)
  ) {
    if (safestRoute !== urlPath) {
      if (safestRoute === routes.FirstLoan) sessionStorage.clear();
      return <Navigate to={safestRoute} />;
    }
  }

  // Show unsafe banner in case route is not on "isRouteAvailableAccordingtoBE" (this only applies on NON PROD environments)
  const showUnsafeRouteBanner =
    availableFromBEValidations === undefined &&
    isNotProduction() &&
    isNotStage();

  return (
    <>
      {showUnsafeRouteBanner && <NoBEValidationsBanner />}
      {children}
    </>
  );
}
